/**
 * A layout: everything placed in a hall for an event. Saved as JSON (on the device now, on the server
 * from milestone 3) in a versioned format, so a layout saved by an older app still opens.
 *
 * Metres and radians in the hall frame. Every placement keeps a full aim (position, height, turn,
 * tilt, roll) and a free specs record, so speakers can be added later without changing saved layouts.
 */

import { catalogueItem, type BodyBuild, type CatalogueItem, type Counted, type Dress, type Mount, type Pose } from './catalogue';
import { FT, IN } from './units';

export const LAYOUT_FORMAT = 'mawkeb-layout';
export const LAYOUT_VERSION = 1;

export interface Aim {
  x: number;
  y: number;
  /** Height of the item's base above the hall floor (the stage top, a platform). */
  z: number;
  /** Turn about the vertical, anticlockwise seen from above; 0 faces the stage. */
  yaw: number;
  tilt: number;
  roll: number;
}

/** An image someone uploaded for a flat item; the file itself lives in the device's store (R2 from milestone 3). */
export interface ImageRef {
  key: string;
  name: string;
  /** Pixels, for the proportions. */
  width: number;
  height: number;
}

export interface PersonParams {
  dress: Dress;
  pose: Pose;
  height: number;
  build: BodyBuild;
}

interface Common {
  id: string;
  item: string;
  aim: Aim;
  locked?: boolean;
  label?: string;
  specs: Record<string, unknown>;
}

export interface PropPlacement extends Common {
  kind: 'prop';
  /** Width, depth and height when changed from the catalogue's (a strip's length, a banner's width). */
  size?: [number, number, number];
  image?: ImageRef;
  person?: PersonParams;
  /** Flat items: on a wall or on a stand, and how high, when changed from the catalogue's. */
  mount?: Mount;
  hangAt?: number;
}

/** Rows of chairs, kept as one piece until broken apart. The aim is the middle of the block. */
export interface RowsPlacement extends Common {
  kind: 'rows';
  perRow: number;
  /** Clear space between neighbouring chairs. */
  gap: number;
  rows: number;
  /** Front of one row to the front of the next. */
  spacing: number;
  /** Width of the centre aisle; 0 for unbroken rows. */
  aisle: number;
}

export type Placement = PropPlacement | RowsPlacement;

export interface Layout {
  format: typeof LAYOUT_FORMAT;
  version: typeof LAYOUT_VERSION;
  hall: string;
  placements: Placement[];
  /** The men's and women's divider, measured from the stage front. */
  divider: { on: boolean; fromStage: number };
}

export class LayoutError extends Error {
  override name = 'LayoutError';
}

export function newLayout(hall: string): Layout {
  return { format: LAYOUT_FORMAT, version: LAYOUT_VERSION, hall, placements: [], divider: { on: true, fromStage: 37 * FT } };
}

export const ROW_DEFAULTS = { perRow: 14, gap: 3 * IN, rows: 6, spacing: 36 * IN, aisle: 5 * FT };

const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Reads a layout from storage or the network, upgrading older versions; throws a LayoutError naming the fault. */
export function readLayout(raw: unknown): Layout {
  const fail = (msg: string): never => {
    throw new LayoutError(`layout: ${msg}`);
  };
  if (!raw || typeof raw !== 'object') fail('not an object');
  const l = raw as Layout;
  if (l.format !== LAYOUT_FORMAT) fail(`unexpected format ${String(l.format)}`);
  if (!num(l.version)) fail('no version');
  if (l.version > LAYOUT_VERSION) fail(`version ${l.version} is newer than this app understands`);
  if (typeof l.hall !== 'string' || !l.hall) fail('no hall');
  if (!Array.isArray(l.placements)) fail('placements missing');
  const seen = new Set<string>();
  for (const p of l.placements) {
    if (!p || typeof p !== 'object' || typeof p.id !== 'string' || !p.id) fail('a placement has no id');
    if (seen.has(p.id)) fail(`placement ${p.id} appears twice`);
    seen.add(p.id);
    const a = p.aim;
    if (!a || ![a.x, a.y, a.z, a.yaw, a.tilt, a.roll].every(num)) fail(`placement ${p.id} has no aim`);
    if (typeof p.item !== 'string') fail(`placement ${p.id} names no item`);
    if (p.kind === 'rows') {
      if (![p.perRow, p.gap, p.rows, p.spacing, p.aisle].every(num) || p.perRow < 1 || p.rows < 1) fail(`rows ${p.id} are incomplete`);
    } else if (p.kind === 'prop') {
      if (p.size && !(Array.isArray(p.size) && p.size.length === 3 && p.size.every((v) => num(v) && v > 0))) fail(`placement ${p.id} has a bad size`);
    } else {
      fail(`placement ${String((p as { id: string }).id)} is of an unknown kind`);
    }
    if (!p.specs || typeof p.specs !== 'object') p.specs = {};
  }
  const d = l.divider;
  if (!d || typeof d.on !== 'boolean' || !num(d.fromStage)) l.divider = { on: true, fromStage: 37 * FT };
  return l;
}

/** The item's size as placed: the catalogue's unless this placement changed it. */
export function placedSize(p: PropPlacement, item: CatalogueItem): [number, number, number] {
  return p.size ? [p.size[0], p.size[1], p.size[2]] : [item.size[0], item.size[1], item.size[2]];
}

/**
 * The catalogue item as this placement has it: a flat item may be moved from a wall to a stand, or hung
 * higher; one that hangs on a host stands on poles while it has none (`specs.host` names its host).
 */
export function placedItem(p: PropPlacement, item: CatalogueItem): CatalogueItem {
  const loose = item.support === 'none' && !p.specs.host;
  if (p.mount === undefined && p.hangAt === undefined && !loose) return item;
  const mount = p.mount ?? item.mount;
  let support = mount === 'stand' ? (item.support ?? 'poles') : item.support;
  if (support === 'none' && !p.specs.host) support = 'poles';
  return { ...item, mount, support, hangAt: p.hangAt ?? item.hangAt };
}

export interface RowsGeometry {
  /** Seat centres in the block's own frame (metres; the block faces +y, its middle at 0, 0). */
  seats: [number, number][];
  width: number;
  depth: number;
}

/** Where every chair of a block of rows stands. */
export function rowsGeometry(r: Pick<RowsPlacement, 'perRow' | 'gap' | 'rows' | 'spacing' | 'aisle'>, chair: readonly [number, number, number]): RowsGeometry {
  const [cw, cd] = chair;
  const n = Math.max(1, Math.round(r.perRow));
  const rows = Math.max(1, Math.round(r.rows));
  const gap = Math.max(0, r.gap);
  const spacing = Math.max(cd, r.spacing);
  const split = r.aisle > 0 && n > 1;
  const left = split ? Math.ceil(n / 2) : n;
  const right = n - left;
  const run = (k: number) => (k > 0 ? k * cw + (k - 1) * gap : 0);
  const width = split ? run(left) + r.aisle + run(right) : run(n);
  const depth = (rows - 1) * spacing + cd;
  const xs: number[] = [];
  for (let i = 0; i < left; i++) xs.push(-width / 2 + cw / 2 + i * (cw + gap));
  for (let i = 0; i < right; i++) xs.push(-width / 2 + run(left) + r.aisle + cw / 2 + i * (cw + gap));
  const seats: [number, number][] = [];
  for (let k = 0; k < rows; k++) {
    const y = depth / 2 - cd / 2 - k * spacing;
    for (const x of xs) seats.push([x, y]);
  }
  return { seats, width, depth };
}

export function seatCount(r: Pick<RowsPlacement, 'perRow' | 'rows'>): number {
  return Math.max(1, Math.round(r.perRow)) * Math.max(1, Math.round(r.rows));
}

/** A point in a placement's own frame, in the hall frame. */
export function toHall(aim: Aim, local: readonly [number, number]): [number, number] {
  const c = Math.cos(aim.yaw);
  const s = Math.sin(aim.yaw);
  return [aim.x + local[0] * c - local[1] * s, aim.y + local[0] * s + local[1] * c];
}

/** The four corners of what a placement covers on the floor, anticlockwise seen from above. */
export function footprint(p: Placement): [number, number][] {
  let w: number;
  let d: number;
  if (p.kind === 'rows') {
    const chair = catalogueItem(p.item)?.size ?? [0.5, 0.5, 0.9];
    ({ width: w, depth: d } = rowsGeometry(p, chair));
  } else {
    const item = catalogueItem(p.item);
    [w, d] = item ? placedSize(p, item) : [0.5, 0.5];
  }
  return [
    toHall(p.aim, [-w / 2, -d / 2]),
    toHall(p.aim, [w / 2, -d / 2]),
    toHall(p.aim, [w / 2, d / 2]),
    toHall(p.aim, [-w / 2, d / 2]),
  ];
}

export type Counts = Record<Counted, number>;

/** What the counts panel shows: chairs (loose and in rows), tables, banners and people. */
export function countLayout(layout: Layout): Counts {
  const out: Counts = { chairs: 0, tables: 0, banners: 0, people: 0 };
  for (const p of layout.placements) {
    if (p.kind === 'rows') {
      out.chairs += seatCount(p);
      continue;
    }
    const counted = catalogueItem(p.item)?.counts;
    if (counted) out[counted] += 1;
  }
  return out;
}
